import { create } from 'zustand';
import type { SidekickMessage, EngagementMode } from '../types/sidekick';

interface SidekickState {
  // Chat
  messages: SidekickMessage[];
  engagementMode: EngagementMode;
  isOpen: boolean;
  isThinking: boolean;

  // Session
  sessionId: string | null;
  stageId: string | null;
  planId: string | null;

  // Moderation / strikes
  strikes: number;
  cooldownUntil: number | null; // timestamp ms
  sessionPaused: boolean;
  sessionEnded: boolean;

  // Unread
  unreadCount: number;

  // Actions
  addMessage: (message: SidekickMessage) => void;
  setEngagementMode: (mode: EngagementMode) => void;
  setOpen: (open: boolean) => void;
  setThinking: (thinking: boolean) => void;
  clearChat: () => void;
  setSession: (planId: string, stageId: string) => void;
  setSessionId: (id: string) => void;
  addStrike: () => number; // returns new strike count
  setCooldown: (until: number) => void;
  clearCooldown: () => void;
  pauseSession: () => void;
  endSession: () => void;
  incrementUnread: () => void;
  clearUnread: () => void;
  setMessages: (messages: SidekickMessage[]) => void;
}

export const useSidekickStore = create<SidekickState>((set, get) => ({
  messages: [],
  engagementMode: 'explore',
  isOpen: false,
  isThinking: false,

  sessionId: null,
  stageId: null,
  planId: null,

  strikes: 0,
  cooldownUntil: null,
  sessionPaused: false,
  sessionEnded: false,

  unreadCount: 0,

  addMessage: (message) =>
    set((state) => {
      const isAssistant = message.role === 'assistant';
      return {
        messages: [...state.messages, message],
        unreadCount:
          isAssistant && !state.isOpen
            ? state.unreadCount + 1
            : state.unreadCount,
      };
    }),
  setEngagementMode: (mode) => set({ engagementMode: mode }),
  setOpen: (open) => set({ isOpen: open, unreadCount: open ? 0 : get().unreadCount }),
  setThinking: (thinking) => set({ isThinking: thinking }),
  clearChat: () =>
    set({
      messages: [],
      engagementMode: 'explore',
      strikes: 0,
      cooldownUntil: null,
      sessionPaused: false,
      sessionEnded: false,
      sessionId: null,
      unreadCount: 0,
    }),
  setSession: (planId, stageId) =>
    set({
      planId,
      stageId,
      messages: [],
      strikes: 0,
      cooldownUntil: null,
      sessionPaused: false,
      sessionEnded: false,
      sessionId: null,
      unreadCount: 0,
      engagementMode: 'explore',
    }),
  setSessionId: (id) => set({ sessionId: id }),
  addStrike: () => {
    const newCount = get().strikes + 1;
    set({ strikes: newCount });
    return newCount;
  },
  setCooldown: (until) => set({ cooldownUntil: until }),
  clearCooldown: () => set({ cooldownUntil: null }),
  pauseSession: () => set({ sessionPaused: true }),
  endSession: () => set({ sessionEnded: true }),
  incrementUnread: () =>
    set((state) => ({ unreadCount: state.unreadCount + 1 })),
  clearUnread: () => set({ unreadCount: 0 }),
  setMessages: (messages) => set({ messages }),
}));
